import type { PropsWithChildren } from 'react';
import React, { createContext, useContext } from 'react';
import { useCmsUsefulLinksQuery } from '@gov-nx/api';
import type { Nullable } from '@gov-nx/core/types';
import { useCmsUrlPath } from '@gov-nx/store';
import { useFilter } from '@gov-nx/utils/common';
import type { PageCode } from '../definitions/codes';
import type { UsefulLinksContextType } from './context.types';

const USEFUL_LINKS_DISPLAY_COUNT = 12;

export const UsefulLinksContext =
	createContext<Nullable<UsefulLinksContextType>>(null);

export function UsefulLinksContextProvider({
	children,
	code,
}: PropsWithChildren<{ code: PageCode }>) {
	const { filter, setPartialFilter } = useFilter<{ maxLinks: number }>({
		key: code,
		initialState: { maxLinks: USEFUL_LINKS_DISPLAY_COUNT },
	});
	const query = useCmsUsefulLinksQuery();
	const { baseCmsUrlPath } = useCmsUrlPath();

	const data = query.data.slice(0, filter.maxLinks);
	const numberOfLinks = query.data.length;
	const numberOfLinksDisplayed = data.length;
	const numberOfLinksLeft = numberOfLinks - numberOfLinksDisplayed;

	const filterIncrementCount =
		numberOfLinksLeft > USEFUL_LINKS_DISPLAY_COUNT
			? USEFUL_LINKS_DISPLAY_COUNT
			: numberOfLinksLeft;

	return (
		<UsefulLinksContext.Provider
			value={{
				code,
				data,
				numberOfLinks,
				numberOfLinksDisplayed,
				filterIncrementCount,
				isLoading: query.isLoading,
				baseCmsUrlPath,
				showMore: () =>
					setPartialFilter({
						maxLinks: filter.maxLinks + filterIncrementCount,
					}),
			}}>
			{children}
		</UsefulLinksContext.Provider>
	);
}

export const useUsefulLinksContext = (): UsefulLinksContextType => {
	const context = useContext(UsefulLinksContext);
	if (!context) {
		throw new Error(
			'useUsefulLinksContext must be used within a UsefulLinksContextProvider'
		);
	}
	return context;
};
